import Footer from "@/components/Footer";
import Navbar from "@/components/Navbar";
import ScrollToTop from "@/components/ScrollToTop";
import HomePage from "@/pages/Index";
import Login from "@/pages/Login";
import Register from "@/pages/Register"; // Import the Register page
import ForgotPasswordPage from "@/pages/ForgotPasswordPage"; // Import ForgotPasswordPage
import ResetPasswordPage from "@/pages/ResetPasswordPage"; // Import ResetPasswordPage
import VerifyEmailPage from "@/pages/VerifyEmailPage"; // Import VerifyEmailPage
import AboutPage from "@/pages/AboutPage"; // Import AboutPage
import ContactPage from "@/pages/ContactPage"; // Import ContactPage
import NotFoundPage from "@/pages/NotFound";
import PropertyListings from "@/pages/PropertyListings";
import PropertyDetailsPage from "@/pages/PropertyView";
import SearchResults from "@/pages/SearchResults";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminListings from "@/pages/admin/AdminListings";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminSettings from "@/pages/admin/AdminSettings";
import CreateUser from "@/pages/admin/CreateUser";
import CreateListing from "@/pages/admin/CreateListing";
import AdminInquiriesPage from "@/pages/admin/AdminInquiriesPage";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import DashboardHome from "@/pages/dashboard/DashboardHome";
import DashboardListings from "@/pages/dashboard/DashboardListings";
import AddListing from "@/pages/dashboard/AddListing";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Route, Routes, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import { PropertyTypesProvider } from "@/context/PropertyTypesContext"; // Add this import
import FormProvider from "@/components/listings/add/FormProvider"; // Import FormProvider

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ScrollToTop />
      <Toaster position="top-right" closeButton richColors />
      <div className="flex flex-col min-h-screen">
        <FormProvider>
          {" "}
          {/* Wrap the entire Routes with FormProvider */}
          <Routes>
            {/* Public routes with Navbar and Footer */}
            <Route
              path="/"
              element={
                <>
                  <Navbar />
                  <main className="flex-grow w-full max-w-7xl mx-auto lg:max-w-none lg:mx-0 py-6">
                    <HomePage />
                  </main>
                  <Footer />
                </>
              }
            />
            <Route
              path="/search"
              element={
                <>
                  <Navbar />
                  <main className="flex-grow w-full max-w-7xl mx-auto lg:max-w-none lg:mx-0 py-6">
                    <SearchResults />
                  </main>
                  <Footer />
                </>
              }
            />
            <Route
              path="/properties"
              element={
                <>
                  <Navbar />
                  <main className="flex-grow w-full max-w-7xl mx-auto lg:max-w-none lg:mx-0 py-6">
                    <PropertyListings />
                  </main>
                  <Footer />
                </>
              }
            />
            <Route
              path="/property/:id"
              element={
                <>
                  <Navbar />
                  <main className="flex-grow w-full max-w-7xl mx-auto lg:max-w-none lg:mx-0 py-6">
                    <PropertyDetailsPage />
                  </main>
                  <Footer />
                </>
              }
            />
            <Route
              path="/about"
              element={
                <main className="flex-grow w-full max-w-7xl mx-auto lg:max-w-none lg:mx-0 py-6">
                  <AboutPage />
                </main>
              }
            />
            <Route
              path="/contact"
              element={
                <>
                  <Navbar />
                  <main className="flex-grow w-full max-w-7xl mx-auto lg:max-w-none lg:mx-0 py-6">
                    <ContactPage />
                  </main>
                  <Footer />
                </>
              }
            />
            <Route
              path="/login"
              element={
                <>
                  <Navbar />
                  <main className="flex-grow w-full max-w-7xl mx-auto lg:max-w-none lg:mx-0 py-6">
                    <Login />
                  </main>
                  <Footer />
                </>
              }
            />
            <Route
              path="/register"
              element={
                // Add the register route
                <>
                  <Navbar />
                  <main className="flex-grow w-full max-w-7xl mx-auto lg:max-w-none lg:mx-0 py-6">
                    <Register />
                  </main>
                  <Footer />
                </>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <>
                  <Navbar />
                  <main className="flex-grow w-full max-w-7xl mx-auto lg:max-w-none lg:mx-0 py-6">
                    <ForgotPasswordPage />
                  </main>
                  <Footer />
                </>
              }
            />
            <Route
              path="/reset-password"
              element={
                <>
                  <Navbar />
                  <main className="flex-grow w-full max-w-7xl mx-auto lg:max-w-none lg:mx-0 py-6">
                    <ResetPasswordPage />
                  </main>
                  <Footer />
                </>
              }
            />
            <Route
              path="/verify-email"
              element={
                <>
                  <Navbar />
                  <main className="flex-grow w-full max-w-7xl mx-auto lg:max-w-none lg:mx-0 py-6">
                    <VerifyEmailPage />
                  </main>
                  <Footer />
                </>
              }
            />

            {/* Dashboard routes with dashboard layout */}
            <Route
              element={
                <PropertyTypesProvider>
                  <DashboardLayout />
                </PropertyTypesProvider>
              }
            >
              {/* User Dashboard Routes */}
              <Route path="/dashboard" element={<DashboardHome />} />
              <Route
                path="/dashboard/listings"
                element={<DashboardListings />}
              />
              {/* Route for adding a new listing - steps before media */}
              <Route
                path="/dashboard/add-listing/new/step/:step"
                element={<AddListing />}
              />
              {/* Route for editing an existing listing or continuing after media step */}
              <Route
                path="/dashboard/add-listing/edit/:listingId/step/:step"
                element={<AddListing />}
              />
              {/* Redirect from the base add-listing path to the first step of the new listing flow */}
              <Route
                path="/dashboard/add-listing"
                element={
                  <Navigate to="/dashboard/add-listing/new/step/0" replace />
                }
              />
              <Route path="/dashboard/inquiries" element={<AdminInquiriesPage />} />

              {/* Admin Routes */}
              <Route path="/admin" element={<AdminDashboard />} />
              <Route
                path="/admin/dashboard"
                element={<Navigate to="/admin" replace />}
              />
              <Route path="/admin/listings" element={<AdminListings />} />
              <Route path="/admin/users" element={<AdminUsers />} />
              <Route path="/admin/settings" element={<AdminSettings />} />
              <Route path="/admin/analytics" element={<AdminAnalytics />} />
              <Route path="/admin/create-user" element={<CreateUser />} />
              <Route path="/admin/create-listing" element={<CreateListing />} />
              <Route path="/admin/inquiries" element={<AdminInquiriesPage />} />
            </Route>

            {/* Catch all */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </FormProvider>{" "}
        {/* Close FormProvider */}
      </div>
    </QueryClientProvider>
  );
}

export default App;
